import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { fetchServices } from '@/lib/api/services'
import {
  addAllowedService,
  packageTypeAllowsServiceQuantity,
  packageTypeUsesCreditAmount,
  packageTypeUsesGiftVoucherRule,
  packageTypeUsesMembershipRule,
  packageTypeUsesPickCount,
  packageTypeUsesServices,
  packageTypeUsesSessionCount,
  removeAllowedService,
  updateAllowedService,
} from '@/lib/masters/packageMaster'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

/**
 * @param {{
 *   form: ReturnType<import('@/lib/masters/packageMaster').emptyPackageForm>
 *   onChange: (next: ReturnType<import('@/lib/masters/packageMaster').emptyPackageForm>) => void
 *   error?: unknown
 * }} props
 */
export function PackageRuleBuilder({ form, onChange, error }) {
  const [services, setServices] = useState([])
  const [loadingServices, setLoadingServices] = useState(true)
  const [selectedServiceId, setSelectedServiceId] = useState('')

  useEffect(() => {
    let active = true
    setLoadingServices(true)

    fetchServices()
      .then((items) => {
        if (active) setServices(items.filter((service) => service.is_active))
      })
      .catch(() => {
        if (active) setServices([])
      })
      .finally(() => {
        if (active) setLoadingServices(false)
      })

    return () => {
      active = false
    }
  }, [])

  const availableServices = useMemo(
    () => services.filter(
      (service) => !form.allowed_services.some((row) => row.service_id === service.id),
    ),
    [form.allowed_services, services],
  )

  function updateRule(field, value) {
    onChange({
      ...form,
      rule: { ...form.rule, [field]: value },
    })
  }

  function handleAddService() {
    const serviceId = Number(selectedServiceId)
    if (!serviceId) return

    const service = services.find((item) => item.id === serviceId)
    if (!service) return

    onChange({
      ...form,
      allowed_services: addAllowedService(form, serviceId, service.name),
    })
    setSelectedServiceId('')
  }

  function handleRemoveService(serviceId) {
    onChange({
      ...form,
      allowed_services: removeAllowedService(form, serviceId),
    })
  }

  function handleServiceField(serviceId, field, value) {
    onChange({
      ...form,
      allowed_services: updateAllowedService(form, serviceId, field, value),
    })
  }

  const ruleError = getFieldError(error, 'rule')

  return (
    <div className="space-y-5 rounded-2xl border border-border/70 bg-muted/20 p-5">
      <div>
        <h2 className="text-sm font-semibold">Package rules</h2>
        <p className="text-xs text-muted-foreground">
          Configure redemption rules and included services for this package type.
        </p>
      </div>

      {packageTypeUsesPickCount(form.type) ? (
        <div>
          <label htmlFor="package-pick-count" className="mb-1.5 block text-sm font-medium">
            Pick count
          </label>
          <input
            id="package-pick-count"
            type="number"
            min={2}
            max={999}
            value={form.rule.pick_count}
            onChange={(e) => updateRule('pick_count', Number(e.target.value) || 2)}
            className={cn(
              'h-11 w-full max-w-xs rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Customer chooses this many services from the allowed list per redemption.
          </p>
        </div>
      ) : null}

      {packageTypeUsesCreditAmount(form.type) ? (
        <div>
          <label htmlFor="package-credit-amount" className="mb-1.5 block text-sm font-medium">
            Wallet credit (AED)
          </label>
          <input
            id="package-credit-amount"
            type="number"
            min={0}
            step="0.01"
            value={form.rule.credit_amount}
            onChange={(e) => updateRule('credit_amount', e.target.value)}
            className={cn(
              'h-11 w-full max-w-xs rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
        </div>
      ) : null}

      {packageTypeUsesSessionCount(form.type) ? (
        <div>
          <label htmlFor="package-session-count" className="mb-1.5 block text-sm font-medium">
            Session count
          </label>
          <input
            id="package-session-count"
            type="number"
            min={1}
            max={9999}
            value={form.rule.session_count}
            onChange={(e) => updateRule('session_count', Number(e.target.value) || 1)}
            className={cn(
              'h-11 w-full max-w-xs rounded-xl border border-border/80 bg-card px-4 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
          />
        </div>
      ) : null}

      {packageTypeUsesMembershipRule(form.type) ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="package-membership-days" className="mb-1.5 block text-sm font-medium">
              Membership period (days)
            </label>
            <input
              id="package-membership-days"
              type="number"
              min={1}
              max={365}
              value={form.rule.membership_period_days}
              onChange={(e) => updateRule('membership_period_days', Number(e.target.value) || 30)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>
          <label className="flex items-end gap-2 pb-3 text-sm">
            <input
              type="checkbox"
              checked={form.rule.rollover_unused}
              onChange={(e) => updateRule('rollover_unused', e.target.checked)}
              className="rounded border-border"
            />
            Rollover unused visits
          </label>
        </div>
      ) : null}

      {packageTypeUsesGiftVoucherRule(form.type) ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.rule.redeem_products}
            onChange={(e) => updateRule('redeem_products', e.target.checked)}
            className="rounded border-border"
          />
          Allow voucher redemption on retail products
        </label>
      ) : null}

      {packageTypeUsesServices(form.type) ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <label htmlFor="package-service-picker" className="mb-1.5 block text-sm font-medium">
                Allowed services
              </label>
              <select
                id="package-service-picker"
                value={selectedServiceId}
                onChange={(e) => setSelectedServiceId(e.target.value)}
                disabled={loadingServices || availableServices.length === 0}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                <option value="">
                  {loadingServices
                    ? 'Loading services…'
                    : availableServices.length === 0
                      ? 'No more services to add'
                      : 'Select a service'}
                </option>
                {availableServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleAddService}
              disabled={!selectedServiceId}
              className={cn(
                'inline-flex h-11 items-center gap-2 rounded-xl border border-border/80 px-4 text-sm font-medium',
                'hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              <Plus className="h-4 w-4" />
              Add service
            </button>
          </div>

          {form.allowed_services.length === 0 ? (
            <p className="text-sm text-muted-foreground">Add at least one service for this package type.</p>
          ) : (
            <ul className="space-y-2">
              {form.allowed_services.map((row) => (
                <li
                  key={row.service_id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-border/70 bg-card px-4 py-3"
                >
                  <span className="min-w-0 flex-1 text-sm font-medium">{row.service_name}</span>
                  {packageTypeAllowsServiceQuantity(form.type) ? (
                    <label className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Qty</span>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={row.quantity}
                        onChange={(e) => handleServiceField(
                          row.service_id,
                          'quantity',
                          Number(e.target.value) || 1,
                        )}
                        className={cn(
                          'h-9 w-20 rounded-lg border border-border/80 bg-background px-2 text-sm',
                          'focus:outline-none focus:ring-2 focus:ring-ring',
                        )}
                      />
                    </label>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleRemoveService(row.service_id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-destructive"
                    aria-label={`Remove ${row.service_name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {ruleError ? (
        <p className="text-sm text-destructive" role="alert">
          {ruleError}
        </p>
      ) : null}
    </div>
  )
}
